// Supplier change emails (docs/reference/SUPPLIER_CHANGE_EMAILS.md): the default subject and
// body for telling a supplier its booking changed. The API works out the changes after an edit
// (SupplierChangeService); the Suppliers tab builds the same shape from the event's builds when
// staff send one by hand. Both open SupplierEmailsDialog, where the body can be edited.
//
// A notice: { supplier, kind, changes: [{ type, service, type_id, before, after, groupsize }], build_ids }
//   kind: 'date_change' | 'booking_change' | 'cancellation'
//   type: 'changed' (before -> after) | 'added' | 'cancelled' | 'current' (no before: sent by hand)
//   before / after: { start_date, end_date, start_time }

import dayjs from 'dayjs'
import { brandFor } from '@/utils/brand'
import { LINK_FALLBACK, accomNightRows, groupsizeLabel, serviceDates, serviceOption } from '@/utils/supplierTemplateEmails'

export const CHANGE_EMAIL_KINDS = {
    date_change: 'Dates Changed',
    booking_change: 'Booking Changed',
    cancellation: 'Cancellation',
}

export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

const fmt = (date) => date ? dayjs(date).format('ddd DD/MM/YYYY') : ''

function nights(dates) {
    if (!dates || !dates.start_date || !dates.end_date) return 0
    return Math.max(dayjs(dates.end_date).diff(dayjs(dates.start_date), 'day'), 0)
}

// "Fri 09/07/2027 – Sun 11/07/2027 (2 nights)" for a hotel, "Sat 10/07/2027 at 20:00" otherwise.
export function describeDates(dates, typeId) {
    if (!dates || !dates.start_date) return '—'
    let text = fmt(dates.start_date)
    if (typeId == 1) {
        const count = nights(dates)
        if (count > 0) text += ` – ${fmt(dates.end_date)} (${count} night${count === 1 ? '' : 's'})`
    }
    if (dates.start_time) text += `${typeId == 1 ? '<br>Check-in' : ' at'} ${escapeHtml(dates.start_time)}`
    return text
}

export function describeGroupsize(groupsize) {
    return groupsizeLabel(groupsize) || '—'
}

// What changed on one service, for the booking change table.
export function describeChange(change) {
    if (change.type === 'added') return 'Added'
    if (change.type === 'cancelled') return 'Cancelled'
    if (change.type !== 'changed' || !change.before || !change.after) return 'Updated'
    const parts = []
    if (change.before.start_date !== change.after.start_date) parts.push('Date changed')
    if (change.type_id == 1 && nights(change.before) !== nights(change.after)) {
        parts.push(`Nights changed (${nights(change.before)} → ${nights(change.after)})`)
    }
    if (String(change.before.start_time || '') !== String(change.after.start_time || '')) parts.push('Time changed')
    return parts.length ? parts.join(', ') : 'Updated'
}

// A change as SupplierEmailsDialog lists it under its supplier, to untick.
export function changeService(change, id) {
    const dates = change.type === 'cancelled' ? change.before : (change.after || change.before)
    const stay = dates ? { ...dates, type_id: change.type_id, accom_groupsize: change.groupsize } : null
    const nights = stay ? accomNightRows(stay) : null
    return {
        id,
        build_id: change.build_id || null,
        name: change.service || '—',
        nights,
        date: stay && !nights ? serviceDates(stay) : null,
        time: dates && dates.start_time ? dates.start_time : null,
        rate: null,
        groupsize: nights ? null : groupsizeLabel(change.groupsize),
        tag: change.type === 'current' ? null : describeChange(change),
        selected: true,
    }
}

// The same shape as an API notice, from the builds shown on the Suppliers tab.
export function noticeFromBuilds(supplier, builds, kind) {
    const cancelled = kind === 'cancellation'
    const dates = (build) => ({
        start_date: build.start_date ? String(build.start_date).slice(0, 10) : null,
        end_date: build.end_date ? String(build.end_date).slice(0, 10) : null,
        start_time: build.start_time || null,
    })
    return {
        supplier,
        kind,
        contacted: true,
        changes: builds.map(build => ({
            type: cancelled ? 'cancelled' : 'current',
            service: build.option ? build.option.name : null,
            type_id: build.type_id,
            before: cancelled ? dates(build) : null,
            after: cancelled ? null : dates(build),
            groupsize: build.type_id == 1 ? build.accom_groupsize : build.groupsize,
            build_id: cancelled ? null : build.id,
        })),
        build_ids: cancelled ? [] : builds.map(build => build.id),
    }
}

const CELL = 'border:1px solid #DDDDDD;padding:6px 8px;color:black;font-size:14px;vertical-align:top;word-break:break-word;'
const HEAD = CELL + 'background-color:#F2F2F2;font-weight:bold;text-align:left;'
const TEXT = 'text-align:left;color:black;font-size:16px;'

function table(headers, rows) {
    return `
                <table contenteditable="false" style="border-collapse:collapse;width:100%;">
                    <tr>${headers.map(h => `<th style="${HEAD}">${h}</th>`).join('')}</tr>
                    ${rows.map(row => `<tr>${row.map(cell => `<td style="${CELL}">${cell}</td>`).join('')}</tr>`).join('\n                    ')}
                </table>`
}

function confirmButton(event, notice, appUrl) {
    if (!notice.build_ids || !notice.build_ids.length || !event || !notice.supplier) return ''
    const href = `${appUrl}/supplier_confirm/${event.id}/${notice.supplier.id}/${notice.build_ids.join(',')}`
    return `<p style="text-align:center;"><a href="${href}" style="display:inline-block;padding:12px 24px;background-color:#4CAF50;color:white;text-decoration:none;border-radius:5px;font-weight:bold;font-size:16px;">Confirm Booking</a><br><br><span style="font-size:13px;">${LINK_FALLBACK}</span><br><a href="${href}" style="font-size:13px;word-break:break-all;">${href}</a></p>`
}

/**
 * { subject, body } for one supplier.
 * options: { event, company, advisorName, appUrl, oldStartDate, newStartDate }
 *   event: an event with lead (name, phone, start_date, location, event_type) and company
 */
export function buildChangeEmail(notice, options = {}) {
    const event = options.event || {}
    const lead = event.lead || {}
    const appUrl = options.appUrl || window.location.origin
    const brand = brandFor(event.company || options.company, lead.event_type).display_name || ''
    const supplierName = notice.supplier ? notice.supplier.supplier_name : ''
    const eventName = event.name || [lead.first_name, lead.last_name].filter(Boolean).join(' ')
    const ref = event.reference_code ? ` (${event.reference_code})` : ''
    const changes = notice.changes || []
    const hasBefore = changes.some(change => change.before && change.type !== 'cancelled')

    const details = [
        `<b>Group:</b> ${escapeHtml(eventName)}${escapeHtml(ref)}`,
        lead.first_name ? `<b>Organiser:</b> ${escapeHtml([lead.first_name, lead.last_name].filter(Boolean).join(' '))}${lead.phone ? ' - ' + escapeHtml(lead.phone) : ''}` : '',
        lead.location && lead.location.name ? `<b>Location:</b> ${escapeHtml(lead.location.name)}` : '',
    ].filter(Boolean).join('<br>')

    let subject
    let intro
    let rows
    let headers
    let outro
    if (notice.kind === 'cancellation') {
        subject = `Cancellation - ${eventName}${ref}`
        intro = `Unfortunately we need to cancel the following booking for <b>${escapeHtml(eventName)}</b>.`
        headers = ['Service', 'Date', 'Groupsize']
        rows = changes.map(change => [escapeHtml(change.service || '—'), describeDates(change.before || change.after, change.type_id), describeGroupsize(change.groupsize)])
        outro = `Please reply to confirm you've received this cancellation.`
    } else if (notice.kind === 'date_change') {
        subject = `Date Change - ${eventName}${ref}`
        const oldDate = options.oldStartDate ? fmt(options.oldStartDate) : ''
        const newDate = options.newStartDate ? fmt(options.newStartDate) : (lead.start_date ? fmt(lead.start_date) : '')
        intro = `The dates for <b>${escapeHtml(eventName)}</b> have changed`
            + (newDate ? `: the group now arrives on <b>${newDate}</b>${oldDate ? ` (previously ${oldDate})` : ''}.` : '.')
            + ` The updated booking is below.`
        headers = hasBefore ? ['Service', 'Previous Date', 'New Date', 'Groupsize'] : ['Service', 'New Date', 'Groupsize']
        rows = changes.map(change => {
            const row = [escapeHtml(change.service || '—')]
            if (hasBefore) row.push(change.before ? describeDates(change.before, change.type_id) : '—')
            row.push(describeDates(change.after, change.type_id), describeGroupsize(change.groupsize))
            return row
        })
        outro = `Please confirm you can take the booking on the new dates.`
    } else {
        subject = `Booking Change - ${eventName}${ref}`
        intro = `There has been a change to the booking for <b>${escapeHtml(eventName)}</b>. The updated booking is below.`
        headers = hasBefore ? ['Service', 'Change', 'Was', 'Now', 'Groupsize'] : ['Service', 'Date', 'Groupsize']
        rows = changes.map(change => hasBefore
            ? [
                escapeHtml(change.service || '—'),
                describeChange(change),
                change.before ? describeDates(change.before, change.type_id) : '—',
                change.type === 'cancelled' ? '<i>Cancelled</i>' : describeDates(change.after, change.type_id),
                describeGroupsize(change.groupsize),
            ]
            : [escapeHtml(change.service || '—'), describeDates(change.after || change.before, change.type_id), describeGroupsize(change.groupsize)])
        outro = `Please confirm the updated booking.`
    }

    const body = `
                <div style="text-align:center;font-size:28px;color:black;"><b>Hello ${escapeHtml(supplierName)},</b></div>
                <br>
                <p style="${TEXT}">${intro}</p>
                <p style="${TEXT}">${details}</p>${table(headers, rows)}
                <p style="${TEXT}">${outro}</p>
                ${notice.kind === 'cancellation' ? '' : confirmButton(event, notice, appUrl)}
                <p style="${TEXT}">
                    Thank you,
                    <br>
                    ${[options.advisorName, brand].filter(Boolean).map(escapeHtml).join(' - ')}
                </p>
            `

    return { subject, body }
}

// Booking statuses meaning the supplier has been asked for or confirmed the booking.
const CONTACTED_STATUSES = [2, 3]

// Deleted services (get_deleted_quote_builds) per supplier, once each: a copy on a variant
// package is the same booking. supplierId: just that supplier's.
export function deletedBuildGroups(deletedBuilds, supplierId = null) {
    const groups = []
    const seen = new Set()
    for (const build of deletedBuilds || []) {
        const key = `${build.supplier_id}|${build.option_id}|${String(build.start_date).slice(0, 10)}`
        if ((supplierId !== null && build.supplier_id !== supplierId) || seen.has(key)) continue
        seen.add(key)
        const group = groups.find(builds => builds[0].supplier_id === build.supplier_id)
        if (group) group.push(build)
        else groups.push([build])
    }
    return groups
}

/**
 * Email Supplier/s → a booking change (sent by hand, from the services as they are now, or the
 * deleted ones for a cancellation): SupplierEmailsDialog's props.
 * groups: [[build, ...], ...] one array per supplier.
 * options: { event, company, advisorName, currency, single, everyone, ticked }
 *   single: one supplier picked from its own button, so it's ticked even if never contacted.
 *   everyone: tick every supplier, not just those already contacted (the events lists).
 *   ticked: build ids picked upfront (the events lists' tick boxes): only those services, and
 *     their suppliers, start ticked.
 */
export function changeEmailDialog(kind, groups, { event, company, advisorName = '', currency = '', single = false, everyone = false, ticked = null }) {
    const cancellation = kind === 'cancellation'
    const emailOptions = { event, company, advisorName }
    const items = groups.filter(builds => builds.length && builds[0].supplier).map(builds => {
        const supplier = builds[0].supplier
        // Declined services are listed unticked; a cancellation lists every deleted one.
        const services = builds.map(build => cancellation
            ? { ...serviceOption(build, currency, true), tag: null }
            : serviceOption(build, currency, ticked ? ticked.includes(build.id) : null))
        const contacted = builds.some(build => CONTACTED_STATUSES.includes(build.booking_status_id))
        return {
            key: supplier.id,
            supplier,
            selected: (single || everyone || !!ticked || contacted) && services.some(service => service.selected),
            contacted,
            services,
            compose: (ids) => {
                const notice = noticeFromBuilds(supplier, builds.filter(build => ids.includes(build.id)), kind)
                const { subject, body } = buildChangeEmail(notice, emailOptions)
                return { subject, body, build_ids: notice.build_ids }
            },
        }
    })
    const title = `${CHANGE_EMAIL_KINDS[kind]} Email`
    return {
        title: single && items.length ? `${title} - ${items[0].supplier.supplier_name}` : `${title}s to Suppliers`,
        subtitle: cancellation
            ? 'Lists each supplier\'s deleted services. Check each email, untick any supplier or service you don\'t want to send, then send.'
            : 'Lists each supplier\'s services as they are now. Suppliers already asked for or confirmed are ticked.',
        email_type: kind,
        items,
        note: cancellation ? '' : 'Sending asks the supplier to confirm again: their services go back to <b>Requested</b> until they click Confirm Booking.',
    }
}
